/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import { Theme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent, SelectProps } from "@mui/material/Select";
import { useTheme } from "../../Theme/Theme";
import { InputLabel, SxProps } from "@mui/material";
import { MdCheck, MdClear } from "react-icons/md";
import React, { useState } from "react";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const getStyles = (option: any, selectedOptions: any[], theme: Theme) => ({
  fontWeight: selectedOptions?.includes(option) ? theme.typography.fontWeightMedium : theme.typography.fontWeightRegular,
});

export type WcMultiselectProps<T> = SelectProps & {
  options: T[];
  onChange: (event: SelectChangeEvent<T[]>) => void;
  getOptionLabel?: (option: T) => string;
  getDisplayLabel?: (option: T) => string;
  renderValue?: (values: T[]) => string;
  clearLabel?: string;
  width?: number | string;
}

export const WcMultiselect = <T,>(props: WcMultiselectProps<T>) => {
  const theme = useTheme();
  const {
    options,
    value,
    onChange,
    getOptionLabel = (option: T) => String(option),
    getDisplayLabel = (option: T) => String(option),
    renderValue = (values: T[]) => values.map(getDisplayLabel).join(", "),
    disabled,
    clearLabel = "Clear All",
    label,
    sx,
    width = "100%",
    size = "medium",
  } = props;

  // State to manage dropdown open/close and local selections
  const [open, setOpen] = useState(false);
  //@ts-ignore
  const [localValue, setLocalValue] = useState<T[]>(value);

  // Handle local changes without triggering onChange
  const handleChange = (event: SelectChangeEvent<T[]>) => {
    setLocalValue(event.target.value as T[]);
    event.stopPropagation();
  };

  // Handle dropdown close - trigger onChange with accumulated changes
  const handleClose = () => {
    setOpen(false);
    if (JSON.stringify(localValue) !== JSON.stringify(value)) {
      const syntheticEvent = {
        target: { value: localValue },
      } as SelectChangeEvent<T[]>;
      onChange(syntheticEvent);
    }
  };

  // Handle clearing all selections
  const handleClearAll = (event: React.MouseEvent) => {
    event.stopPropagation();
    setLocalValue([]);
    const syntheticEvent = {
      target: { value: [] },
    } as SelectChangeEvent<T[]>;
    onChange(syntheticEvent);
  };

  // Update local value when prop value changes
  React.useEffect(() => {
    //@ts-ignore
    setLocalValue(value);
  }, [value]);

  return (
    <FormControl sx={{ width: width, ...sx }}>
      {label && (
        <InputLabel
          sx={{
            padding: 2,
          }}
        >
          {label}
        </InputLabel>
      )}
      <Select
        multiple
        size={size}
        value={localValue}
        defaultValue={localValue}
        onChange={handleChange}
        // input={<OutlinedInput label={label} />}
        MenuProps={MenuProps}
        disabled={disabled}
        renderValue={renderValue}
        sx={{
          padding: 2,
        }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        {...props}
      >
        {/* Add Clear All option at the top */}
        {localValue.length > 0 && (
          <MenuItem
            value=""
            onClick={handleClearAll}
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              color: theme.palette.error.main,
              fontWeight: theme.typography.fontWeightMedium,
              padding: 5,
            }}
          >
            <MdClear style={{ marginRight: 8 }} />
            {clearLabel}
          </MenuItem>
        )}

        {options.map((option: T) => (
          <MenuItem key={getOptionLabel(option)} value={getOptionLabel(option)} style={getStyles(option, [], theme as unknown as Theme)}>
            {getDisplayLabel(option)}
            {localValue.includes(option) && (
              <MdCheck
                style={{
                  marginLeft: "auto",
                  color: theme.palette.primary.main,
                }}
              />
            )}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
